import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, ArrowLeft } from "lucide-react";
import { api, waLink } from "../lib/api";
import { useConfig } from "../lib/config";
import { Seo } from "../components/site/Seo";

function renderContent(content) {
  const blocks = [];
  let list = [];
  const flushList = (key) => {
    if (list.length) {
      blocks.push(
        <ul key={`ul-${key}`} className="space-y-2 my-4">
          {list.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-zinc-700 leading-relaxed">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0"></span>
              {item}
            </li>
          ))}
        </ul>
      );
      list = [];
    }
  };
  content.split("\n").forEach((line, idx) => {
    const t = line.trim();
    if (!t) return;
    if (t.startsWith("## ")) {
      flushList(idx);
      blocks.push(<h2 key={idx} className="text-xl font-black text-zinc-950 mt-10 mb-3">{t.slice(3)}</h2>);
    } else if (t.startsWith("- ")) {
      list.push(t.slice(2));
    } else {
      flushList(idx);
      blocks.push(<p key={idx} className="text-sm text-zinc-600 leading-relaxed my-4">{t}</p>);
    }
  });
  flushList("end");
  return blocks;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const config = useConfig();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArticle(null);
    setNotFound(false);
    api.get(`/articles/${slug}`).then((r) => setArticle(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="py-32 text-center" data-testid="article-not-found">
        <h1 className="text-2xl font-black text-zinc-950">Artículo no encontrado</h1>
        <Link to="/blog" className="text-brand-700 font-bold text-sm underline mt-4 inline-block">Volver al blog</Link>
      </main>
    );
  }
  if (!article) {
    return <main className="py-32 text-center text-sm text-zinc-500" data-testid="article-loading">Cargando artículo…</main>;
  }

  const wa = waLink(config.whatsapp_number || "", `Hola, leí el artículo "${article.title}" y tengo una consulta sobre mi acuario.`);

  return (
    <main className="bg-white" data-testid="blog-post-page">
      <Seo
        title={`${article.title} — AZOO Costa Rica`}
        description={article.meta_description || article.excerpt}
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-bold text-brand-700 hover:text-brand-600 transition-colors mb-8" data-testid="article-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Todos los consejos
        </Link>

        <h1 className="text-3xl sm:text-4xl font-black text-zinc-950 leading-tight" data-testid="article-title">{article.title}</h1>
        {article.excerpt && <p className="text-sm text-zinc-500 mt-4 leading-relaxed">{article.excerpt}</p>}

        {article.image && (
          <div className="aspect-video rounded-2xl overflow-hidden bg-zinc-100 my-8">
            <img src={article.image} alt={article.title} className="w-full h-full object-cover" data-testid="article-image" />
          </div>
        )}

        <div data-testid="article-content">{renderContent(article.content || "")}</div>

        <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-zinc-950 text-white flex flex-col sm:flex-row items-center justify-between gap-6" data-testid="article-whatsapp-box">
          <div className="space-y-2 text-center sm:text-left">
            <h2 className="font-black text-lg">¿Tienes dudas sobre tu acuario?</h2>
            <p className="text-sm text-zinc-400">Escríbenos por WhatsApp y te ayudamos a elegir el producto AZOO correcto.</p>
          </div>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-500 hover:bg-brand-400 text-zinc-950 font-black text-xs uppercase tracking-wider transition-colors" data-testid="article-whatsapp-button">
            <MessageCircle className="w-4 h-4" /> Consultar
          </a>
        </div>
      </article>
    </main>
  );
}
